/**
 * AggregatePanel — sidebar section listing every aggregate declared on
 * the current model. For each row shows grain, storage table, and a
 * governance snapshot (hit count, avg query ms, zero-hit warnings,
 * drift status).
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Layers, RefreshCcw, Trash2, ShieldCheck, ShieldAlert, TrendingUp, AlertTriangle, Settings2, PlayCircle, ExternalLink, Sparkles, Pencil, History, XCircle, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { AggregateWizard } from './AggregateWizard';

const _OP_SYM = { equals: '=', not_equals: '≠', in: 'in', gt: '>', gte: '≥', lt: '<', lte: '≤', is_null: 'is null', not_null: 'is not null' };
const _NULL_OP = (op) => op === 'is_null' || op === 'not_null';
// Per-measure flow/level tags for the panel list.
function measureTags(measureMap) {
  return Object.entries(measureMap || {}).map(([name, spec]) => ({
    name,
    level: (spec || {}).additivity === 'semi_additive',
  }));
}

// Watermark/freshness lag badge for an aggregate binding.
function freshnessBadge(a) {
  const r = a.refresh || {};
  const last = r.last_refreshed_at;
  const sla = r.freshness_sla_minutes || 1440;
  if (!last) {
    return { tone: 'muted', label: 'freshness n/a', title: `External ETL owns refresh${r.lag_watermark_column ? ` · watermark: ${r.lag_watermark_column}` : ''}` };
  }
  const lagMin = Math.max(0, (Date.now() - new Date(last).getTime()) / 60000);
  const human = lagMin < 60 ? `${Math.round(lagMin)}m` : lagMin < 1440 ? `${Math.round(lagMin / 60)}h` : `${Math.round(lagMin / 1440)}d`;
  const stale = lagMin > sla;
  return {
    tone: stale ? 'red' : 'emerald',
    label: stale ? `stale · ${human}` : `fresh · ${human} ago`,
    title: `Last refreshed ${new Date(last).toLocaleString()} · SLA ${sla}m`
      + (r.lag_watermark_column ? ` · watermark: ${r.lag_watermark_column}` : '')
      + (r.last_source_watermark != null ? ` @ ${r.last_source_watermark}` : ''),
  };
}
// Distinct baked-predicate conditions across an aggregate's measures. A
// baked_predicate may be a flat array of leaves (legacy) OR a nested boolean
// tree {bool, args:[...]} | {not: node} | leaf — the latter produced by the
// "group conditions" builder. Flatten either shape to its leaf conditions so
// the panel never crashes on a tree.
function _collectPredLeaves(node, acc) {
  if (!node) return;
  if (Array.isArray(node)) { node.forEach(n => _collectPredLeaves(n, acc)); return; }
  if (typeof node !== 'object') return;
  if (Array.isArray(node.args)) { node.args.forEach(n => _collectPredLeaves(n, acc)); return; }
  if (node.not) { _collectPredLeaves(node.not, acc); return; }
  if (node.field) acc.push(node);
}
function predicateConditions(measureMap) {
  const seen = new Set();
  const out = [];
  Object.values(measureMap || {}).forEach(spec => {
    const leaves = [];
    _collectPredLeaves(spec?.baked_predicate, leaves);
    leaves.forEach(c => {
      if (!c.field) return;
      const s = _NULL_OP(c.operator)
        ? `${c.field} ${_OP_SYM[c.operator]}`
        : `${c.field} ${_OP_SYM[c.operator] || c.operator} ${Array.isArray(c.value) ? c.value.join('/') : c.value}`;
      if (!seen.has(s)) { seen.add(s); out.push(s); }
    });
  });
  return out;
}

// Short relative time for the audit trail ("3d ago", "just now").
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.round(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AggregatePanel({ modelId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editAgg, setEditAgg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [policy, setPolicy] = useState({
    hybrid_union: 'when_query_touches_today',
    sketch_tolerance: 'prefer_fast',
    drift_threshold_pct: 0.1,
    fallback_on_drift: true,
  });
  const [modelOverrides, setModelOverrides] = useState({});   // keys explicitly set at model
  const [tenantDefault, setTenantDefault] = useState({});     // keys set tenant-wide
  const [policySources, setPolicySources] = useState({});     // per-key: model|tenant|default
  const [policyLoading, setPolicyLoading] = useState(false);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const res = await api.getAggregateGovernance(modelId);
      setSummary(res);
    } catch (e) { toast.error(e.message || 'Failed to load aggregates'); }
    finally { setLoading(false); }
    try { setHealth(await api.getAggregateHealth(modelId)); } catch (e) { /* optional */ }
  }, [modelId]);

  const loadPolicy = useCallback(async () => {
    if (!modelId) return;
    try {
      const res = await api.getAggregatePolicy(modelId);
      if (res.aggregate_policy) setPolicy(res.aggregate_policy);
      setModelOverrides(res.model_overrides || {});
      setTenantDefault(res.tenant_default || {});
      setPolicySources(res.sources || {});
    } catch (e) { /* silent — policy is optional */ }
  }, [modelId]);

  const loadSuggestions = useCallback(async () => {
    if (!modelId) return;
    try {
      const res = await api.getAggregateSuggestions(modelId, 7);
      setSuggestions(res.proposals || []);
    } catch (e) { /* silent — no log yet */ }
  }, [modelId]);

  useEffect(() => { load(); loadPolicy(); loadSuggestions(); }, [load, loadPolicy, loadSuggestions]);

  const savePolicy = async () => {
    setPolicyLoading(true);
    try {
      // Send only the keys the user has explicitly changed at the model
      // level. Other keys inherit from the tenant default (or built-in).
      const res = await api.updateAggregatePolicy(modelId, { overrides: modelOverrides });
      if (res.aggregate_policy) setPolicy(res.aggregate_policy);
      setPolicySources(res.sources || {});
      toast.success('Policy saved — new queries will use these settings');
      setPolicyOpen(false);
    } catch (e) { toast.error(e.message); }
    finally { setPolicyLoading(false); }
  };

  // Change a single policy key at the model level. Passing `null` clears
  // the override and lets the tenant default (or built-in) take over.
  const setOverride = (key, value) => {
    setModelOverrides(prev => {
      const next = { ...prev };
      if (value === null || value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
    setPolicy(prev => {
      const inherited = tenantDefault[key];
      const builtin = { hybrid_union: 'when_query_touches_today', sketch_tolerance: 'prefer_fast', drift_threshold_pct: 0.1, fallback_on_drift: true }[key];
      return { ...prev, [key]: value !== null && value !== undefined ? value : (inherited !== undefined ? inherited : builtin) };
    });
  };

  const repairBindings = async () => {
    setLoading(true);
    try {
      const res = await api.repairAggregateBindings(modelId);
      const relinked = (res.repaired || []).filter(r => r.relinked).length;
      toast.success(`Repaired ${res.count} binding${res.count !== 1 ? 's' : ''}${relinked > 0 ? ` — re-linked ${relinked} canonical${relinked !== 1 ? 's' : ''}` : ''}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const runAllDriftChecks = async () => {
    setLoading(true);
    try {
      const res = await api.runAllAggregateDriftChecks(modelId);
      const unhealthy = (res.results || []).filter(r => r.healthy === false).length;
      toast.success(`Checked ${res.checked} aggregate${res.checked !== 1 ? 's' : ''}${unhealthy > 0 ? ` — ${unhealthy} drifted` : ' — all healthy'}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const runDriftCheck = async (eid) => {
    setBusyId(eid);
    try {
      const res = await api.runAggregateDriftCheck(eid);
      toast.success(res.healthy
        ? `No drift detected (max ${res.max_diff_pct}%)`
        : `Drift detected: ${res.max_diff_pct}% on ${res.matched_keys} keys, ${res.only_in_source} missing, ${res.only_in_aggregate} extra`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const refresh = async (eid) => {
    setBusyId(eid);
    try {
      const res = await api.refreshAggregate(eid);
      toast.success(`Refreshed — ${res.built_rows} rows`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const remove = async (eid, name) => {
    if (!window.confirm(`Delete aggregate "${name}"? This does NOT drop the underlying table.`)) return;
    setBusyId(eid);
    try {
      await api.deleteAggregate(eid);
      toast.success('Aggregate removed');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const aggregates = summary?.aggregates || [];
  return (
    <div className="border-t border-border/30" data-testid="aggregate-panel">
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Layers className="w-3 h-3 text-primary/70" />
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Aggregates</p>
          {aggregates.length > 0 && <Badge variant="outline" className="text-[8px]">{aggregates.length}</Badge>}
          {summary && summary.total_decisions > 0 && <span className="text-[9px] text-muted-foreground/80">· {summary.total_decisions} decisions</span>}
        </div>
        <div className="flex items-center gap-1">
          {aggregates.length > 0 && (
            <button onClick={runAllDriftChecks} disabled={loading} className="p-0.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground disabled:opacity-40" title="Recheck drift on every aggregate" data-testid="drift-all-btn"><PlayCircle className="w-3 h-3" /></button>
          )}
          <button onClick={repairBindings} disabled={loading} className="p-0.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground disabled:opacity-40" title="Repair bindings — regenerate canonical measures so fine-grain queries use the transaction fact (fixes imported models)" data-testid="repair-bindings-btn"><Wrench className="w-3 h-3" /></button>
          <button onClick={() => setPolicyOpen(true)} className="p-0.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground" title="Router policy for this model" data-testid="policy-btn"><Settings2 className="w-3 h-3" /></button>
          <Link to={`/semantic-models/${modelId}/aggregates`} className="p-0.5 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground" title="Open full governance dashboard" data-testid="gov-link"><ExternalLink className="w-3 h-3" /></Link>
          <button onClick={() => { setEditAgg(null); setWizardOpen(true); }} className="text-primary" data-testid="add-aggregate-btn"><Plus className="w-3 h-3" /></button>
        </div>
      </div>

      {loading && aggregates.length === 0 && <div className="px-3 pb-2 text-[10px] text-muted-foreground/80">Loading…</div>}
      {!loading && aggregates.length === 0 && (
        <div className="px-3 pb-3 text-[10px] text-muted-foreground/80 leading-relaxed">
          No aggregates yet. Aggregates route common queries to a smaller, pre-summed table for 10–1000× faster response.
        </div>
      )}

      {health && !health.healthy && (
        <div className="mx-2 mb-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[10px]" data-testid="aggregate-health-banner">
          <div className="flex items-center gap-1 mb-1"><AlertTriangle className="w-2.5 h-2.5 text-red-400" /><span className="text-red-300 font-medium">{health.count} aggregate binding{health.count !== 1 ? 's' : ''} need repair</span></div>
          <p className="text-muted-foreground/80 mb-1">Fine-grain queries are falling back to the summary table. {health.issues?.[0]?.detail}</p>
          <button onClick={repairBindings} disabled={loading} className="text-red-300 hover:underline disabled:opacity-50" data-testid="health-repair-link">Repair bindings →</button>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mx-2 mb-2 p-2 rounded-lg bg-primary/5 border border-primary/20 text-[10px]" data-testid="suggestions-banner">
          <div className="flex items-center gap-1 mb-1"><Sparkles className="w-2.5 h-2.5 text-primary" /><span className="text-primary font-medium">{suggestions.length} suggested aggregate{suggestions.length !== 1 ? 's' : ''}</span></div>
          <p className="text-muted-foreground/70 mb-1">The router logged repeat misses that a new aggregate would&#39;ve caught.</p>
          <Link to={`/semantic-models/${modelId}/aggregates`} className="text-primary hover:underline" data-testid="review-suggestions">Review suggestions →</Link>
        </div>
      )}

      <div className="px-2 pb-2 space-y-1.5 max-h-[360px] overflow-y-auto" data-testid="aggregate-list">
        {aggregates.map((a, ai) => {
          const drift = a.drift_state;
          const driftHealthy = drift?.healthy;
          const driftStale = !drift || (new Date() - new Date(drift.last_check_at)) > 7 * 24 * 3600 * 1000;
          const preds = predicateConditions(a.measure_map);
          return (
            <div key={a.aggregate_id || a.agg_id || `${a.entity_id}-${a.name || ai}`} className="p-2 rounded-lg bg-muted/10 border border-border/20 text-[10px] space-y-1" data-testid={`agg-row-${a.entity_id}-${ai}`}>
              <div className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium truncate">{a.name}</p>
                  <p className="font-mono text-muted-foreground/70 truncate">{a.table_name}</p>
                  {a.summary_entity_name && (
                    <p className="text-[9px] text-muted-foreground/80 truncate" data-testid={`agg-summary-${a.entity_id}`}>
                      ↳ binds <span className="text-primary/80">{a.summary_entity_name}</span>{a.summary_provisioned ? ' · managed' : ' · external'}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => { setEditAgg(a); setWizardOpen(true); }} className="p-1 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground" title="Edit aggregate" data-testid={`edit-${a.entity_id}`}><Pencil className="w-3 h-3" /></button>
                  {(a.refresh || {}).kind === 'managed' && (
                    <button onClick={() => refresh(a.entity_id)} disabled={busyId === a.entity_id} className="p-1 rounded hover:bg-muted/40 text-muted-foreground hover:text-foreground disabled:opacity-40" title="Rebuild from canonical" data-testid={`refresh-${a.entity_id}`}><RefreshCcw className="w-3 h-3" /></button>
                  )}
                  <button onClick={() => runDriftCheck(a.entity_id)} disabled={busyId === a.entity_id} className={`p-1 rounded hover:bg-muted/40 disabled:opacity-40 ${driftHealthy ? 'text-emerald-400' : drift && !driftHealthy ? 'text-red-400' : 'text-muted-foreground hover:text-foreground'}`} title="Run drift check vs canonical" data-testid={`drift-${a.entity_id}`}>
                    {driftHealthy ? <ShieldCheck className="w-3 h-3" /> : drift && !driftHealthy ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                  </button>
                  <button onClick={() => remove(a.entity_id, a.name)} disabled={busyId === a.entity_id} className="p-1 rounded hover:bg-muted/40 text-muted-foreground hover:text-destructive disabled:opacity-40" title="Remove aggregate" data-testid={`del-${a.entity_id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              {/* Validation — canonical/grain/measure columns that no longer exist */}
              {a.validation && a.validation.status !== 'ok' && (
                <div className={`mt-0.5 p-1.5 rounded border text-[9px] ${a.validation.status === 'error' ? 'bg-red-500/10 border-red-500/25 text-red-300' : 'bg-amber-500/10 border-amber-500/25 text-amber-300'}`} data-testid={`agg-validation-${a.entity_id}`}>
                  <div className="flex items-center gap-1 font-medium">
                    {a.validation.status === 'error' ? <XCircle className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                    {a.validation.status === 'error' ? 'Needs attention' : 'Check definition'} — edit to fix
                  </div>
                  <ul className="mt-0.5 space-y-0.5 pl-3 list-disc marker:text-current/60">
                    {a.validation.issues.map((iss, i) => <li key={i} data-testid={`agg-validation-issue-${a.entity_id}-${i}`}>{iss.detail}</li>)}
                  </ul>
                </div>
              )}
              {/* Guard: backend flagged an unrenderable predicate shape */}
              {a._predicate_guard && a._predicate_guard.renderable === false && (
                <div className="mt-0.5 p-1.5 rounded border text-[9px] bg-amber-500/10 border-amber-500/25 text-amber-300" data-testid={`agg-predicate-guard-${a.entity_id}`}>
                  <div className="flex items-center gap-1 font-medium">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    Unusual filter shape — edit to review
                  </div>
                  <p className="mt-0.5 pl-3">Measure(s) {(a._predicate_guard.unrenderable_measures || []).join(', ')} store a filter this panel can't fully display. Open the editor to inspect it.</p>
                </div>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                {preds.length > 0
                  ? preds.map((p, i) => <Badge key={i} variant="outline" className="text-[8px] font-mono border-amber-500/30 text-amber-300/90" title="Baked predicate condition" data-testid={`agg-predicate-${a.entity_id}-${i}`}>{p}</Badge>)
                  : <Badge variant="outline" className="text-[8px] text-muted-foreground/80" title="No baked predicate — plain rollup">no predicate</Badge>}
                <Badge variant="outline" className="text-[8px]">{a.row_count_estimate?.toLocaleString() ?? '—'} rows</Badge>
                <Badge variant="outline" className="text-[8px]">{(a.refresh || {}).kind || 'managed'}</Badge>
                {(() => { const f = freshnessBadge(a); return f ? (
                  <Badge variant="outline"
                    className={`text-[8px] ${f.tone === 'red' ? 'border-red-500/40 text-red-300' : f.tone === 'emerald' ? 'border-emerald-500/40 text-emerald-300' : 'text-muted-foreground/80'}`}
                    title={f.title} data-testid={`agg-freshness-${a.entity_id}`}>
                    {f.tone === 'red' ? '⚠ ' : '◷ '}{f.label}
                  </Badge>
                ) : null; })()}
                {(a.refresh || {}).next_refresh_at && (
                  <Badge variant="outline" className="text-[8px] text-primary/70" title={`Next auto-refresh at ${a.refresh.next_refresh_at}`}>
                    ⏱ {new Date(a.refresh.next_refresh_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Badge>
                )}
              </div>
              {/* Per-measure roll-up behavior: flow (additive) vs level (semi-additive) */}
              {measureTags(a.measure_map).length > 0 && (
                <div className="flex items-center gap-1 flex-wrap" data-testid={`agg-measures-${a.entity_id}`}>
                  {measureTags(a.measure_map).map((mt, i) => (
                    <Badge key={i} variant="outline"
                      className={`text-[8px] font-mono ${mt.level ? 'border-violet-500/40 text-violet-300' : 'border-sky-500/30 text-sky-300/90'}`}
                      title={mt.level ? 'Semi-additive (level) — LAST across time, SUM across dims' : 'Additive (flow) — SUM across time & dims'}
                      data-testid={`agg-measure-tag-${a.entity_id}-${mt.name}`}>
                      {mt.name} · {mt.level ? 'level' : 'flow'}
                    </Badge>
                  ))}
                </div>
              )}
              {/* Conformed dimension mappings + summary time grain */}
              {(Object.keys(a.dimension_map || {}).length > 0 || a.time_grain) && (
                <div className="flex items-center gap-1 flex-wrap" data-testid={`agg-dimmap-${a.entity_id}`}>
                  {a.time_grain && (
                    <Badge variant="outline" className="text-[8px] font-mono border-teal-500/40 text-teal-300/90"
                      title="Summary temporal grain — finer queries route to the transaction fact">
                      grain · {a.time_grain}
                    </Badge>
                  )}
                  {Object.entries(a.dimension_map || {}).map(([sd, cc]) => {
                    const label = (cc && typeof cc === 'object') ? `${cc.part}(${cc.col})` : cc;
                    return (
                    <Badge key={sd} variant="outline" className="text-[8px] font-mono border-indigo-500/30 text-indigo-300/90"
                      title="Conformed dimension mapping (summary → canonical)"
                      data-testid={`agg-dimmap-tag-${a.entity_id}-${sd}`}>
                      {sd} → {label}
                    </Badge>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-between text-muted-foreground/70">
                <span className="flex items-center gap-1"><TrendingUp className="w-2.5 h-2.5" />{a.hit_count} hits{a.avg_ms ? ` · ${a.avg_ms}ms avg` : ''}</span>
                {a.zero_hit_warning && (
                  <span className="text-amber-400 flex items-center gap-1" title="Router has never picked this aggregate — misdesigned grain?"><AlertTriangle className="w-2.5 h-2.5" />zero-hit</span>
                )}
              </div>
              {drift && !driftHealthy && (
                <div className="mt-1 p-1.5 rounded bg-red-500/10 border border-red-500/20 text-[9px] text-red-300">
                  Drift {drift.max_diff_pct}% · {drift.matched_keys} matched · {drift.only_in_source} missing · {drift.only_in_aggregate} extra
                </div>
              )}
              {a.top_rejection_reasons?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[9px] text-muted-foreground/80 hover:text-foreground">Why the router sometimes skips this ({a.top_rejection_reasons.reduce((s, r) => s + r.count, 0)} times)</summary>
                  <ul className="mt-1 space-y-0.5 pl-2 text-[9px] text-muted-foreground/70">
                    {a.top_rejection_reasons.map((r, i) => <li key={i}>· {r.reason} <span className="text-muted-foreground/70">({r.count})</span></li>)}
                  </ul>
                </details>
              )}
              {/* Audit trail — who last changed grain/measures and when */}
              {a.last_edited_at && (
                <details className="mt-1" data-testid={`agg-audit-${a.entity_id}`}>
                  <summary className="cursor-pointer text-[9px] text-muted-foreground/80 hover:text-foreground flex items-center gap-1">
                    <History className="w-2.5 h-2.5" />Edited by {a.last_edited_by || 'someone'} · {relTime(a.last_edited_at)}
                  </summary>
                  <ul className="mt-1 space-y-1 pl-2 text-[9px] text-muted-foreground/70">
                    {(a.edit_history || []).map((h, i) => (
                      <li key={i} data-testid={`agg-audit-entry-${a.entity_id}-${i}`}>
                        <span className="text-muted-foreground/90">{h.by_name || h.by || 'someone'}</span>
                        <span className="text-muted-foreground/60"> · {relTime(h.at)}</span>
                        <div className="pl-2 text-muted-foreground/70">{(h.changes || []).join(' · ')}</div>
                      </li>
                    ))}
                    {(a.edit_history || []).length === 0 && <li className="italic text-muted-foreground/50">No detailed history recorded.</li>}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </div>

      <AggregateWizard open={wizardOpen} onOpenChange={(v) => { setWizardOpen(v); if (!v) setEditAgg(null); }} modelId={modelId} onCreated={load} editAggregate={editAgg} />

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent className="max-w-lg" data-testid="policy-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Settings2 className="w-4 h-4 text-primary" />Router Policy</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-1">
            <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
              These settings apply to every aggregate on this model. Values not overridden here inherit from
              the <span className="text-primary">tenant defaults</span>; values not set at either level fall back to built-in defaults.
              Tap the × next to a value to remove that model override.
            </p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Hybrid Union</p>
                <PolicyOriginBadge source={policySources.hybrid_union} onClear={modelOverrides.hybrid_union !== undefined ? () => setOverride('hybrid_union', null) : null} />
              </div>
              <Select value={policy.hybrid_union} onValueChange={(v) => setOverride('hybrid_union', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="policy-hybrid-union"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Always — union the tail on every aggregate hit (safest, slower)</SelectItem>
                  <SelectItem value="when_stale">When stale — only if watermark past SLA</SelectItem>
                  <SelectItem value="when_query_touches_today">When query touches today — recommended default</SelectItem>
                  <SelectItem value="off">Off — accept staleness silently</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Sketch Tolerance</p>
                <PolicyOriginBadge source={policySources.sketch_tolerance} onClear={modelOverrides.sketch_tolerance !== undefined ? () => setOverride('sketch_tolerance', null) : null} />
              </div>
              <Select value={policy.sketch_tolerance} onValueChange={(v) => setOverride('sketch_tolerance', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="policy-sketch-tolerance"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prefer_fast">Prefer fast — route AVG/DISTINCT/percentile to sketches</SelectItem>
                  <SelectItem value="prefer_exact">Prefer exact — force fallback to canonical for approximations</SelectItem>
                  <SelectItem value="per_measure">Per measure — future granular override</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Drift threshold %</p>
                  <PolicyOriginBadge source={policySources.drift_threshold_pct} onClear={modelOverrides.drift_threshold_pct !== undefined ? () => setOverride('drift_threshold_pct', null) : null} />
                </div>
                <Input type="number" step="0.01" min="0" value={policy.drift_threshold_pct} onChange={(e) => setOverride('drift_threshold_pct', parseFloat(e.target.value || '0'))} className="h-8 text-xs" data-testid="policy-drift-threshold" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fallback on drift</p>
                  <PolicyOriginBadge source={policySources.fallback_on_drift} onClear={modelOverrides.fallback_on_drift !== undefined ? () => setOverride('fallback_on_drift', null) : null} />
                </div>
                <div className="flex items-center gap-2 h-8">
                  <Checkbox id="fallback-drift" checked={policy.fallback_on_drift} onCheckedChange={(v) => setOverride('fallback_on_drift', !!v)} data-testid="policy-fallback-drift" />
                  <label htmlFor="fallback-drift" className="text-xs text-foreground cursor-pointer">Enabled</label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyOpen(false)} className="h-8">Cancel</Button>
            <Button onClick={savePolicy} disabled={policyLoading} className="h-8" data-testid="policy-save">{policyLoading ? '…' : 'Save Policy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PolicyOriginBadge({ source, onClear }) {
  if (!source) return null;
  const style = source === 'model'
    ? 'bg-primary/10 text-primary border-primary/30'
    : source === 'tenant'
      ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30'
      : 'bg-muted/30 text-muted-foreground border-border/40';
  const label = source === 'model' ? 'model override' : source === 'tenant' ? 'tenant default' : 'built-in';
  return (
    <div className="flex items-center gap-1">
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider border ${style}`}>{label}</span>
      {source === 'model' && onClear && (
        <button onClick={onClear} className="text-[10px] text-muted-foreground hover:text-destructive" title="Remove model override — inherit from tenant/built-in" data-testid={`policy-clear-${label.replace(/\s+/g, '-')}`}>×</button>
      )}
    </div>
  );
}
